import { Component, Inject, OnDestroy, OnInit } from '@angular/core';

import { FormBuilder, FormGroup, UntypedFormGroup, Validators } from '@angular/forms';

import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { translate } from '@jsverse/transloco';
import { Subscription } from 'rxjs';

import { DropdownObject, EnumErrorStates, ErrorObject } from '@inmoment/the-kitchen';

import { Widget } from 'app/providers/bridge-api/dashboard.service';
import { StorageAccountService } from 'app/providers/storage-api/blender-account.service';
import { Field, FieldService } from 'app/providers/storage-api/field.service';
import {
  injectComponentTelemetryService,
  EnableComponentTelemetry,
  EnableClassTelemetry,
} from 'app/services/otel.service';
import { ErrorObjectUtils } from 'app/utils/error-object.utils';

import { ModalCloseType } from '../../../../enums';
import { WidgetDataSource } from '../../../../models';
import { AnalyticItemsDialogOptions } from '../analytic-item-selector/analytic-item-selector.component';

export interface BigNumberMetadataDialogOptions {
  widget: Widget;
  widgetDataSource: WidgetDataSource;
  newWidget: boolean;
  metadataFields: string[];
}

export interface BigNumberMetadataDialogResult {
  fieldOne: {
    analyticItem: string;
    metadataItemDisplayName: string;
    useFieldDisplayName: boolean;
    customField: boolean;
  };
  fieldTwo: {
    analyticItem: string;
    metadataItemDisplayName: string;
    useFieldDisplayName: boolean;
    customField: boolean;
  };
}

interface BigNumberMetadataFormType {
  fieldOne: UntypedFormGroup;
  fieldTwo: UntypedFormGroup;
}

@EnableClassTelemetry()
@Component({
  selector: 'app-big-number-metadata-selector-dialog',
  templateUrl: './big-number-metadata-selector-dialog.component.html',
  styleUrls: ['./big-number-metadata-selector-dialog.component.scss'],
  standalone: false,
})
export class BigNumberMetadataSelectorDialogComponent implements OnInit, OnDestroy {
  @EnableComponentTelemetry() otelService = injectComponentTelemetryService();
  bigNumberMetadataForm: FormGroup<BigNumberMetadataFormType>;

  private groupFieldsSub$: Subscription;
  metaDataFields: Field[] = [];
  metadataDropdownOne: DropdownObject[] = [
    { title: translate('widgets.big-number-metadata-selector.documents'), id: 'documents' },
  ];
  metadataDropdownTwo: DropdownObject[] = [
    { title: '', id: 'null' },
    { title: translate('widgets.big-number-metadata-selector.documents'), id: 'documents' },
  ];
  metadataFieldsAvailable: boolean;
  readOnlyField = false;
  public formStatuses = {
    displayNameOne: <ErrorObject>{ state: EnumErrorStates.SUCCESS },
    displayNameTwo: <ErrorObject>{ state: EnumErrorStates.SUCCESS },
  };
  newWidget: boolean;
  ModalCloseType = ModalCloseType;

  constructor(
    private fb: FormBuilder,
    private accountService: StorageAccountService,
    private dialogRef: MatDialogRef<AnalyticItemsDialogOptions>,
    @Inject(MAT_DIALOG_DATA) private options: BigNumberMetadataDialogOptions
  ) {}

  get fieldOne() {
    return this.bigNumberMetadataForm.get('fieldOne');
  }

  get fieldTwo() {
    return this.bigNumberMetadataForm.get('fieldTwo');
  }

  get metadataFieldOne() {
    return this.fieldOne.get('metadataField');
  }

  get metadataFieldTwo() {
    return this.fieldTwo.get('metadataField');
  }

  get displayNameOne() {
    return this.fieldOne.get('displayName');
  }

  get displayNameTwo() {
    return this.fieldTwo.get('displayName');
  }

  get useFieldDisplayNameOne() {
    return this.fieldOne.get('useFieldDisplayName');
  }

  get useFieldDisplayNameTwo() {
    return this.fieldTwo.get('useFieldDisplayName');
  }

  getMetadataFieldFor(field: keyof BigNumberMetadataFormType) {
    return this.bigNumberMetadataForm.get(field).get('metadataField');
  }

  getDisplayNameFor(field: keyof BigNumberMetadataFormType) {
    return this.bigNumberMetadataForm.get(field).get('displayName');
  }

  getUseFieldDisplayNameFor(field: keyof BigNumberMetadataFormType) {
    return this.bigNumberMetadataForm.get(field).get('useFieldDisplayName');
  }

  ngOnInit() {
    const widgetPayload = this.options.widget.payload;
    this.newWidget = this.options.newWidget;
    const customFields = this.options.metadataFields;

    this.groupFieldsSub$ = this.accountService.groupFieldsSub.subscribe((accountMetadata) => {
      accountMetadata.metadata.forEach((f) => {
        if (
          (f.fieldType === 'rating' || FieldService.isNumericField(f)) &&
          f.metadata?.display !== false &&
          customFields?.includes(f.fieldName)
        ) {
          this.metaDataFields.push(f);
          [this.metadataDropdownOne, this.metadataDropdownTwo].forEach((arr) =>
            arr.push({
              title: f.description,
              id: f.fieldName,
            })
          );
        }
      });
      this.metadataFieldsAvailable = this.metadataDropdownOne.length > 1;

      const matchingField = (savedAnalyticItem: string): Field => {
        return this.metaDataFields.find((field) => {
          return field.fieldName === savedAnalyticItem;
        });
      };
      const analyticItem = widgetPayload.analyticItem;
      const analyticItemTwo = widgetPayload.analyticItemTwo;
      const analyticItemDisplayName = widgetPayload.metadataDisplayNameOne;
      const analyticItemDisplayNameTwo = widgetPayload.metadataDisplayNameTwo;
      const useFieldDisplayNameOne = widgetPayload.useFieldDisplayNameOne;
      const useFieldDisplayNameTwo = widgetPayload.useFieldDisplayNameTwo;
      const resolveDisplayName = (savedDisplayName: string | null, fieldName: string): string => {
        if (savedDisplayName) {
          return savedDisplayName;
        }
        if (fieldName === 'documents') {
          return translate('widgets.big-number-metadata-selector.count');
        }
        return matchingField(fieldName).label;
      };
      const fieldOneDisplayName = resolveDisplayName(
        analyticItemDisplayName,
        analyticItem ?? 'documents'
      );
      const fieldTwoDisplayName = analyticItemTwo
        ? resolveDisplayName(analyticItemDisplayNameTwo, analyticItemTwo)
        : null;
      this.bigNumberMetadataForm = this.fb.group({
        fieldOne: this.fb.group({
          metadataField: [analyticItem ?? 'documents', Validators.required],
          displayName: [fieldOneDisplayName, Validators.required],
          useFieldDisplayName: [
            {
              value: useFieldDisplayNameOne,
              disabled: analyticItem === 'documents',
            },
          ],
        }),
        fieldTwo: this.fb.group({
          metadataField: [analyticItemTwo ?? null],
          displayName: [
            {
              value: fieldTwoDisplayName,
              disabled: true,
            },
          ],
          useFieldDisplayName: [
            {
              value: useFieldDisplayNameTwo,
              disabled: true,
            },
          ],
        }),
      });
      this.matchFieldDisplayNamesToFields();
      this.validateForm();
      this.setUseFieldDisplayName('fieldOne');
      this.setUseFieldDisplayName('fieldTwo');
    });
  }

  private matchFieldDisplayNamesToFields() {
    if (this.useFieldDisplayNameOne.value) {
      this.displayNameOne.setValue(FieldService.getFieldLongName(this.metadataFieldOne.value));
    }
    if (this.useFieldDisplayNameTwo.value) {
      this.displayNameTwo.setValue(FieldService.getFieldLongName(this.metadataFieldTwo.value));
    }
  }

  validateForm() {
    //If no field two dropdown value, clear and disabled display value
    if (!this.fieldTwo.get('metadataField').value) {
      this.displayNameTwo.clearValidators();
      this.displayNameTwo.updateValueAndValidity();
      this.displayNameTwo.setValue(null);
      this.displayNameTwo.disable();
    }
    //Else, enable display value
    else {
      this.displayNameTwo.enable();
      this.displayNameTwo.addValidators(Validators.required);
      const isSpecialValue =
        this.metadataFieldTwo.value === 'null' || this.metadataFieldTwo.value === 'documents';
      isSpecialValue ? this.useFieldDisplayNameTwo.disable() : this.useFieldDisplayNameTwo.enable();
    }
    //If not metadata fields available, disabled field two and set fieldOne dropdown
    // to documents
    if (!this.metadataFieldsAvailable) {
      this.fieldTwo.disable();
      this.metadataFieldOne.setValue('documents');
      this.readOnlyField = true;
    }
    if (this.displayNameOne.hasError('required')) {
      ErrorObjectUtils.setError(
        this.formStatuses.displayNameOne,
        translate('widgets.big-number-metadata-selector.display-name-required')
      );
    } else {
      ErrorObjectUtils.setNoError(this.formStatuses.displayNameOne);
    }
    if (this.displayNameTwo.hasError('required')) {
      ErrorObjectUtils.setError(
        this.formStatuses.displayNameTwo,
        translate('widgets.big-number-metadata-selector.display-name-required')
      );
    } else {
      ErrorObjectUtils.setNoError(this.formStatuses.displayNameTwo);
    }
  }

  onMetadataDropdownChange(fieldName: keyof BigNumberMetadataFormType, option: DropdownObject) {
    setTimeout(() => {
      const formField = this.bigNumberMetadataForm.get(fieldName);
      if (option.id === 'null') {
        formField.patchValue({ metadataField: undefined });
      } else {
        if (formField.get('metadataField').value === 'documents') {
          formField.patchValue({
            displayName: translate('widgets.big-number-metadata-selector.count'),
          });
        } else {
          const matchingField = this.metaDataFields.find((field) => {
            return field.fieldName === option.id;
          });
          formField.patchValue({
            displayName: matchingField.description,
          });
        }
      }
      const isSpecialValue =
        option.id === 'null' || formField.get('metadataField').value === 'documents';
      isSpecialValue
        ? formField.get('useFieldDisplayName').disable()
        : formField.get('useFieldDisplayName').enable();
      this.setUseFieldDisplayName(fieldName);
      this.validateForm();
    });
  }

  onDisplayNameChange(fieldName: keyof BigNumberMetadataFormType) {
    this.setUseFieldDisplayName(fieldName);
    this.validateForm();
  }

  private setUseFieldDisplayName(fieldName: keyof BigNumberMetadataFormType) {
    const fieldControl = this.getMetadataFieldFor(fieldName);
    const displayNameControl = this.getDisplayNameFor(fieldName);
    const useDisplayNameControl = this.getUseFieldDisplayNameFor(fieldName);

    const isDisplayNameEqualToFieldNameLongName =
      displayNameControl.value === FieldService.getFieldLongName(fieldControl.value);

    useDisplayNameControl.setValue(isDisplayNameEqualToFieldNameLongName);
  }

  onUseFieldDisplayNameChange(field: keyof BigNumberMetadataFormType, isChecked: boolean) {
    const displayNameControl = this.getUseFieldDisplayNameFor(field);
    const metadataFieldControl = this.getMetadataFieldFor(field);
    if (isChecked) {
      displayNameControl.setValue(FieldService.getFieldLongName(metadataFieldControl.value));
    }
  }

  onCancel(closeType = ModalCloseType.CANCEL) {
    this.dialogRef.close(closeType);
  }

  onSave() {
    this.dialogRef.close({
      fieldOne: {
        analyticItem: this.metadataFieldOne.value,
        metadataItemDisplayName: this.displayNameOne.value,
        useFieldDisplayName: this.useFieldDisplayNameOne.value,
        customField: this.metadataFieldOne.value !== 'documents',
      },
      fieldTwo: {
        analyticItem: this.metadataFieldTwo.value ? this.metadataFieldTwo.value : undefined,
        metadataItemDisplayName: this.displayNameTwo.value ? this.displayNameTwo.value : undefined,
        useFieldDisplayName: this.useFieldDisplayNameTwo.value,
        customField: this.metadataFieldTwo.value
          ? this.metadataFieldTwo.value !== 'documents'
          : undefined,
      },
      subtypes: '',
      standardField: false,
    });
  }

  ngOnDestroy(): void {
    this.groupFieldsSub$.unsubscribe();
  }
}
